import React, { useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Colors, Spacing, BorderRadius, FontSizes, FontWeights, FontStacks } from '../constants/theme';

// Micro-interaction keyframes
const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(79, 70, 229, 0.4); }
  50% { box-shadow: 0 0 0 8px rgba(79, 70, 229, 0); }
`;

const slideInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const wiggle = keyframes`
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(-3deg); }
  75% { transform: rotate(3deg); }
`;

const typewriter = keyframes`
  from { width: 0; }
  to { width: 100%; }
`;

const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
`;

const bounce = keyframes`
  0%, 20%, 53%, 80%, 100% { transform: translateY(0); }
  40%, 43% { transform: translateY(-8px); }
  70% { transform: translateY(-4px); }
  90% { transform: translateY(-2px); }
`;

// Enhanced Button with micro-interactions
export const InteractiveButton = styled.button<{
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
}>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.sm}px;
  font-family: ${FontStacks.primary};
  
  padding: ${props => {
    switch (props.size) {
      case 'sm': return `${Spacing.sm}px ${Spacing.md}px`;
      case 'lg': return `${Spacing.md}px ${Spacing.xl}px`;
      default: return `${Spacing.sm}px ${Spacing.lg}px`;
    }
  }};
  
  background: ${props => {
    switch (props.variant) {
      case 'secondary': return Colors.surface;
      case 'ghost': return 'transparent';
      default: return Colors.secondary;
    }
  }};
  
  color: ${props => {
    switch (props.variant) {
      case 'secondary': return Colors.textSecondary;
      case 'ghost': return Colors.textTertiary;
      default: return Colors.textOnPrimary;
    }
  }};
  
  border: 1px solid ${props => {
    switch (props.variant) {
      case 'secondary': return Colors.border;
      case 'ghost': return 'transparent';
      default: return Colors.secondary;
    }
  }};
  
  border-radius: ${BorderRadius.sm}px;
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  cursor: ${props => props.loading ? 'wait' : 'pointer'};
  transition: all 150ms ease;
  
  &:hover {
    background: ${props => {
      switch (props.variant) {
        case 'secondary': return Colors.surfaceHover;
        case 'ghost': return Colors.surfaceHover;
        default: return Colors.accent;
      }
    }};
    border-color: ${props => {
      switch (props.variant) {
        case 'secondary': return Colors.borderDark;
        case 'ghost': return Colors.border;
        default: return Colors.accent;
      }
    }};
    color: ${props => {
      switch (props.variant) {
        case 'secondary': return Colors.textPrimary;
        case 'ghost': return Colors.textPrimary;
        default: return Colors.textOnPrimary;
      }
    }};
  }
  
  &:active {
    transform: translateY(1px);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${Colors.borderFocus}40;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    
    &:hover {
      background: ${props => {
        switch (props.variant) {
          case 'secondary': return Colors.surface;
          case 'ghost': return 'transparent';
          default: return Colors.secondary;
        }
      }};
    }
  }
  
  ${props => props.loading && css`
    opacity: 0.7;
    pointer-events: none;
    
    &::after {
      content: '';
      position: absolute;
      width: 14px;
      height: 14px;
      border: 2px solid transparent;
      border-top: 2px solid currentColor;
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }
    
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
  `}
`;

// Animated Counter
interface AnimatedCounterProps {
  value: number;
  duration?: number;
  formatter?: (value: number) => string;
}

export const AnimatedCounter: React.FC<AnimatedCounterProps> = ({ 
  value, 
  duration = 1500,
  formatter = (v) => v.toLocaleString()
}) => {
  const [displayValue, setDisplayValue] = React.useState(0);
  const [isVisible, setIsVisible] = React.useState(false);
  const elementRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    let startTime: number;
    let startValue = displayValue;

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentValue = startValue + (value - startValue) * easeOutQuart;
      
      setDisplayValue(Math.floor(currentValue));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [value, duration, isVisible, displayValue]);

  return (
    <span ref={elementRef} aria-live="polite">
      {formatter(displayValue)}
    </span>
  );
};

// Staggered Animation Container
export const StaggerContainer = styled.div<{ delay?: number }>`
  & > * {
    opacity: 0;
    transform: translateY(20px);
    animation: ${slideInUp} 0.6s ease-out forwards;
  }
  
  & > *:nth-child(1) { animation-delay: 0ms; }
  & > *:nth-child(2) { animation-delay: 100ms; }
  & > *:nth-child(3) { animation-delay: 200ms; }
  & > *:nth-child(4) { animation-delay: 300ms; }
  & > *:nth-child(5) { animation-delay: 400ms; }
  & > *:nth-child(6) { animation-delay: 500ms; }
  & > *:nth-child(7) { animation-delay: 600ms; }
  & > *:nth-child(8) { animation-delay: 700ms; }
`;

// Morphing Icon Button
export const MorphingIcon = styled.button<{ isActive?: boolean }>`
  width: 44px;
  height: 44px;
  border: none;
  background: transparent;
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 300ms ease;
  
  &:hover {
    background: ${Colors.surfaceHover};
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  ${props => props.isActive && css`
    animation: ${wiggle} 0.5s ease-in-out;
  `}
`;

// Progress Ring
export const ProgressRing: React.FC<{
  progress: number;
  size?: number;
  strokeWidth?: number;
  color?: string;
}> = ({ progress, size = 60, strokeWidth = 4, color = Colors.primary }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <svg width={size} height={size} className="progress-ring">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke={Colors.borderLight}
        strokeWidth={strokeWidth}
        fill="transparent"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke={color}
        strokeWidth={strokeWidth}
        fill="transparent"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        style={{
          transition: 'stroke-dashoffset 0.8s ease-in-out',
          transform: 'rotate(-90deg)',
          transformOrigin: '50% 50%',
        }}
      />
    </svg>
  );
};

// Floating Action Button with micro-interactions
export const FloatingActionButton = styled.button`
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${Colors.primary};
  color: ${Colors.textOnPrimary};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  box-shadow: 0 8px 25px -8px ${Colors.primary}60;
  transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1000;
  
  &:hover {
    transform: scale(1.1) translateY(-2px);
    box-shadow: 0 12px 35px -12px ${Colors.primary}80;
    animation: ${bounce} 1s ease-in-out;
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  &:focus {
    outline: none;
    animation: ${pulseGlow} 1.5s infinite;
  }
`;

// Tooltip with micro-interactions
export const Tooltip = styled.div<{ visible?: boolean }>`
  position: absolute;
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: ${Spacing.sm}px ${Spacing.md}px;
  border-radius: ${BorderRadius.sm}px;
  font-size: 12px;
  white-space: nowrap;
  z-index: 1000;
  pointer-events: none;
  
  opacity: ${props => props.visible ? 1 : 0};
  transform: ${props => props.visible ? 'translateY(0)' : 'translateY(8px)'};
  transition: all 200ms ease;
  
  &::before {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 4px 4px 0 4px;
    border-color: rgba(0, 0, 0, 0.9) transparent transparent transparent;
  }
`;

export default {
  InteractiveButton,
  AnimatedCounter,
  StaggerContainer,
  MorphingIcon,
  ProgressRing,
  FloatingActionButton,
  Tooltip,
};